import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const PopulatingMinimalCanonicalCardDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PopulatingMinimalCanonicalCard"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"idCard"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"card"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"idCard"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"idBoard"}},{"kind":"Field","name":{"kind":"Name","value":"idList"}},{"kind":"Field","name":{"kind":"Name","value":"cardRole"}},{"kind":"Field","name":{"kind":"Name","value":"due"}},{"kind":"Field","name":{"kind":"Name","value":"dueComplete"}},{"kind":"Field","name":{"kind":"Name","value":"isTemplate"}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"start"}},{"kind":"Field","name":{"kind":"Name","value":"url"}},{"kind":"Field","name":{"kind":"Name","value":"badges"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"due"}},{"kind":"Field","name":{"kind":"Name","value":"dueComplete"}},{"kind":"Field","name":{"kind":"Name","value":"start"}}]}},{"kind":"Field","name":{"kind":"Name","value":"board"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"url"}},{"kind":"Field","name":{"kind":"Name","value":"prefs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"backgroundBottomColor"}},{"kind":"Field","name":{"kind":"Name","value":"backgroundBrightness"}},{"kind":"Field","name":{"kind":"Name","value":"backgroundImage"}},{"kind":"Field","name":{"kind":"Name","value":"backgroundImageScaled"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"width"}},{"kind":"Field","name":{"kind":"Name","value":"height"}},{"kind":"Field","name":{"kind":"Name","value":"url"}}]}},{"kind":"Field","name":{"kind":"Name","value":"backgroundTile"}},{"kind":"Field","name":{"kind":"Name","value":"backgroundTopColor"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"list"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"name"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PopulatingMinimalCanonicalCard","document":PopulatingMinimalCanonicalCardDocument}} as const;
export type PopulatingMinimalCanonicalCardQueryVariables = Types.Exact<{
  idCard: Types.Scalars['ID'];
}>;


export type PopulatingMinimalCanonicalCardQuery = (
  { __typename: 'Query' }
  & { card?: Types.Maybe<(
    { __typename: 'Card' }
    & Pick<Types.Card, 'id' | 'idBoard' | 'idList' | 'cardRole' | 'due' | 'dueComplete' | 'isTemplate' | 'name' | 'start' | 'url'>
    & { badges: (
      { __typename: 'Card_Badges' }
      & Pick<Types.Card_Badges, 'due' | 'dueComplete' | 'start'>
    ), board: (
      { __typename: 'Board' }
      & Pick<Types.Board, 'id' | 'name' | 'url'>
      & { prefs?: Types.Maybe<(
        { __typename: 'Board_Prefs' }
        & Pick<Types.Board_Prefs, 'backgroundBottomColor' | 'backgroundBrightness' | 'backgroundImage' | 'backgroundTile' | 'backgroundTopColor'>
        & { backgroundImageScaled?: Types.Maybe<Array<(
          { __typename: 'Board_Prefs_BackgroundImageScaled' }
          & Pick<Types.Board_Prefs_BackgroundImageScaled, 'width' | 'height' | 'url'>
        )>> }
      )> }
    ), list: (
      { __typename: 'List' }
      & Pick<Types.List, 'id' | 'name'>
    ) }
  )> }
);

/**
 * __usePopulatingMinimalCanonicalCardQuery__
 *
 * To run a query within a React component, call `usePopulatingMinimalCanonicalCardQuery` and pass it any options that fit your needs.
 * When your component renders, `usePopulatingMinimalCanonicalCardQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePopulatingMinimalCanonicalCardQuery({
 *   variables: {
 *      idCard: // value for 'idCard'
 *   },
 * });
 */
export function usePopulatingMinimalCanonicalCardQuery(
  baseOptions: TrelloQueryHookOptions<
    PopulatingMinimalCanonicalCardQuery,
    PopulatingMinimalCanonicalCardQueryVariables
  >,
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PopulatingMinimalCanonicalCardDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    PopulatingMinimalCanonicalCardQuery,
    PopulatingMinimalCanonicalCardQueryVariables
  >(PopulatingMinimalCanonicalCardDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePopulatingMinimalCanonicalCardLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PopulatingMinimalCanonicalCardQuery,
    PopulatingMinimalCanonicalCardQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    PopulatingMinimalCanonicalCardQuery,
    PopulatingMinimalCanonicalCardQueryVariables
  >(PopulatingMinimalCanonicalCardDocument, options);
}
export type PopulatingMinimalCanonicalCardQueryHookResult = ReturnType<
  typeof usePopulatingMinimalCanonicalCardQuery
>;
export type PopulatingMinimalCanonicalCardLazyQueryHookResult = ReturnType<
  typeof usePopulatingMinimalCanonicalCardLazyQuery
>;
export type PopulatingMinimalCanonicalCardQueryResult = Apollo.QueryResult<
  PopulatingMinimalCanonicalCardQuery,
  PopulatingMinimalCanonicalCardQueryVariables
>;
